// CandidateForm.js - Updated for Build3 Internship Application
import React, { useState, useEffect } from 'react';
import { uploadCVFile, saveCandidateData, logoutUser, extractCVDataWithProgress, getInterviewLink, getPsychometricInterviewLink , extractCompanyAndJobFromUrl, updateCandidateListSnapshot  } from './utils';
import { doc, getDoc, updateDoc, arrayUnion, setDoc,deleteDoc,collection,getDocs  } from 'firebase/firestore';
import { auth, googleProvider, firestore, storage } from './firebase';
import './CandidateForm.css';

const CandidateForm = ({
  user,
  existingData,
  welcomeMessage,
  isExistingCandidate,
  onSubmissionSuccess,
  isEditMode = false,
  submittedSuccess = false,
  isInterviewDone = false,
  isMetricDone = false
}) => {
  const [cvFile, setCvFile] = useState(null);
  const [cvUploaded, setCvUploaded] = useState(false);
  const [uploadedCvInfo, setUploadedCvInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [extractionProgress, setExtractionProgress] = useState(null);
  const [extractionInProgress, setExtractionInProgress] = useState(false);
  const [interviewLoading, setInterviewLoading] = useState(false);
  const [interviewError, setInterviewError] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(submittedSuccess);

  // Form data state for all job application fields
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    phone: '',
    linkedin: '',
    location: '',

    // Education
    education: '',

    // Job Preferences
    desiredRoles: '',
    salaryCurrency: '',
    salaryAmount: '',
    salaryPeriod: '',
    workLocationPreference: '',
    joiningTimeline: '',

    // Terms
    termsAccepted: false
  });

  // Currency options
  const currencyOptions = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD', 'SGD'];

  // Salary period options
  const salaryPeriodOptions = ['Year', 'Month', 'Day'];

  // Sync read-only state when submittedSuccess changes
  useEffect(() => {
    setIsReadOnly(submittedSuccess);
  }, [submittedSuccess]);

  // Pre-fill form data when existing data is available
  useEffect(() => {
    if (existingData) {
      // console.log('📝 Pre-filling form with existing data:', existingData);

      setFormData({
        firstName: existingData.firstName || '',
        lastName: existingData.lastName || '',
        phone: existingData.phone || '',
        linkedin: existingData.linkedin || '',
        location: existingData.location || '',
        education: existingData.education || '',
        desiredRoles: existingData.desiredRoles || '',
        salaryCurrency: existingData.salaryCurrency || '',
        salaryAmount: existingData.salaryAmount || '',
        salaryPeriod: existingData.salaryPeriod || '',
        workLocationPreference: existingData.workLocationPreference || '',
        joiningTimeline: existingData.joiningTimeline || '',
        termsAccepted: existingData.termsAccepted || false
      });

      // If CV info exists, set it up
      if (existingData.cvFileName && existingData.cvDownloadURL) {
        setCvUploaded(true);
        setUploadedCvInfo({
          fileName: existingData.cvFileName,
          downloadURL: existingData.cvDownloadURL
        });
        // console.log('📄 CV info pre-loaded:', {
        //   fileName: existingData.cvFileName,
        //   downloadURL: existingData.cvDownloadURL
        // });
      }
    }
  }, [existingData]);


const handleFileChange = async (e) => {
  const file = e.target.files[0];
  if (file) {
    // Enhanced file type validation that matches your utils.js contentType mapping
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    const allowedExtensions = ['pdf', 'doc', 'docx'];
    const fileExtension = file.name.toLowerCase().split('.').pop();
    
    // Check both MIME type and file extension for better validation
    const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension);
    
    if (isValidType) {
      setCvFile(file);
      setCvUploaded(false);

      // STEP 1: Upload file to storage
      setUploadLoading(true);
      setExtractionProgress(null);
      const existingCvUrl = uploadedCvInfo?.downloadURL || null;
      const result = await uploadCVFile(file, user.email, existingCvUrl);

      if (result.success) {
        setCvUploaded(true);
        setUploadLoading(false);

        // Store initial CV info without extraction data
        setUploadedCvInfo({
          fileName: result.fileName,
          downloadURL: result.downloadURL,
          file: result.file,
          extractionPending: true
        });

        // STEP 2: Extract CV data in background with progress
        setExtractionInProgress(true);
        setExtractionProgress({ stage: 'starting', message: 'Preparing to extract CV text...' });

        const extractionResult = await extractCVDataWithProgress(result.file, (progress) => {
          setExtractionProgress(progress);
        });

        // Update uploaded CV info with extraction data
        setUploadedCvInfo(prev => ({
          ...prev,
          cvRawData: extractionResult.data || extractionResult.message,
          cvExtractionStatus: extractionResult.status,
          cvExtractionMessage: extractionResult.message,
          extractionPending: false
        }));

        setExtractionInProgress(false);

        // Clear progress message after 3 seconds if successful
        if (extractionResult.success) {
          setTimeout(() => setExtractionProgress(null), 3000);
        }
      } else {
        alert('Failed to upload CV: ' + result.error);
        setUploadLoading(false);
      }
    } else {
      alert('Please upload a PDF or Word document only (.pdf, .doc, .docx)');
      e.target.value = '';
    }
  }
};

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!cvUploaded) {
      alert('Please upload your CV first');
      return;
    }
    
    // Check required fields
    const requiredFields = [
      'firstName', 'lastName', 'phone', 'linkedin', 'location',
      'desiredRoles', 'salaryCurrency', 'salaryAmount', 'salaryPeriod',
      'workLocationPreference', 'joiningTimeline'
    ];

    const emptyFields = requiredFields.filter(field => !formData[field] || (typeof formData[field] === 'string' && !formData[field].trim()));

    if (emptyFields.length > 0) {
      alert('Please fill in all required fields: ' + emptyFields.join(', '));
      return;
    }

    // Additional validation for specific fields
    if (formData.desiredRoles.trim().length < 50) {
      alert('Please provide a detailed description of desired roles (minimum 50 characters)');
      return;
    }

    if (formData.workLocationPreference.trim().length > 250) {
      alert('Work location preference must be maximum 250 characters (approximately 50 words)');
      return;
    }

    const joiningDays = parseInt(formData.joiningTimeline);
    if (isNaN(joiningDays) || joiningDays < 0 || joiningDays > 365) {
      alert('Please enter a valid joining timeline between 0 and 365 days');
      return;
    }

    const salaryAmount = parseFloat(formData.salaryAmount);
    if (isNaN(salaryAmount) || salaryAmount <= 0) {
      alert('Please enter a valid salary amount');
      return;
    }

    if (!formData.termsAccepted) {
      alert('Please accept the Terms and Conditions');
      return;
    }
    
    setLoading(true);
    setSubmitStatus('');
    
    const result = await saveCandidateData(user.email, formData, uploadedCvInfo);

    // console.log('📤 saveCandidateData result:', result);
    
    if (result.success) {
      // console.log('🎉 SUCCESS: Application submitted successfully!');
      setSubmitStatus('success');
      setIsReadOnly(true);

      onSubmissionSuccess({
        ...formData,
        cvFileName: uploadedCvInfo?.fileName,
        cvDownloadURL: uploadedCvInfo?.downloadURL
      }, isExistingCandidate || isEditMode);

      // Snapshot only on NEW submission (not edits/updates)
      if (!isExistingCandidate && !isEditMode) {
        // New candidate — add to 000000_tracking with createdAt = updatedAt = now
        await updateCandidateListSnapshot('new', user.email);
      } else {
        // Existing candidate updating — only updatedAt changes in 000000_tracking
        await updateCandidateListSnapshot('updated', user.email);
      }
    } else {
      // console.log('❌ FAILURE: Build3 application submission failed');
      setSubmitStatus('error');
      alert('Failed to submit application: ' + result.error);
    }
    
    setLoading(false);
  };

  const handleLogout = async () => {
    await logoutUser();
    window.location.reload();
  };

  const handleTakeInterview = async () => {
    setInterviewLoading(true);
    setInterviewError(null);
    const idToken = await user.getIdToken();
    const result = await getInterviewLink();
    if (result.success) {
      const finalUrl = `${result.url}&firebaseToken=${idToken}`;
      window.location.href = finalUrl;
    } else {
      setInterviewError(result.error);
      setInterviewLoading(false);
    }
  };
//   const handleTakeInterview = async () => {
//   setInterviewLoading(true);
//   setInterviewError(null);

//   try {
//      const idToken = await user.getIdToken();
//     //  console.log("Generated ID Token for interview link:", idToken);
//     const localUrl =
//       "http://localhost:3001/?data=%7B%22CompanyName%22%3A%22CandidateTalent%22%2C%22JobNumber%22%3A0%2C%22JobName%22%3A%22Candidate%20General%20Profile%20_ID%3D1770148261646fqdxn5zt8%22%7D";
//     const finalUrl = `${localUrl}&firebaseToken=${idToken}`;
//     //  console.log("finalUrl", finalUrl);
//     window.location.href = finalUrl ;
//   } catch (error) {
//     setInterviewError("Failed to open interview.");
//     setInterviewLoading(false);
//   }
// };

  const [psychometricLoading, setPsychometricLoading] = useState(false);
  const [psychometricError, setPsychometricError] = useState(null);

  const handleTakePsychometric = async () => {
    setPsychometricLoading(true);
    setPsychometricError(null);
     const idToken = await user.getIdToken();
    const result = await getPsychometricInterviewLink();
    if (result.success) {
      const finalUrl = `${result.url}&firebaseToken=${idToken}`;
      window.location.href = finalUrl;
    } else {
      setPsychometricError(result.error);
      setPsychometricLoading(false);
    }
  };

// const handleTakePsychometric = async () => {
//   setPsychometricLoading(true);
//   setPsychometricError(null);
//   try {
//     const idToken = await user.getIdToken();
//     const localUrl =
//       "http://localhost:3001/?data=%7B%22CompanyName%22%3A%22Pmetric%22%2C%22JobNumber%22%3A0%2C%22JobName%22%3A%22Candidate%20Psychometric_ID%3D177215044969315pq0m6sh%22%7D";
//     const finalUrl = `${localUrl}&firebaseToken=${idToken}`;
//     // console.log("finalUrl", finalUrl);
//     window.location.href = finalUrl;
//   } catch (err) {
//     setPsychometricError(err.message);
//     setPsychometricLoading(false);
//   }
// }; 
                                                             
const handleDeleteCandidate = async () => {

  const confirmDelete = window.confirm(
    "Are you sure you want to remove your Skill Graph?"
  );

  if (!confirmDelete) return;

  try {

    const emailKey = user.email.replace(/\./g, "_");
    
    // get interview link (same logic you already use)
    const result = await getInterviewLink();

    if (!result.success) {
      alert("Unable to determine job information.");
      return;
    }

    // extract company + job from URL
    const info = await  extractCompanyAndJobFromUrl(result.url);

    if (!info) {
      alert("Failed to parse interview URL.");
      return;
    }

    const { company, jobName } = info;

        // console.log("Deleting candidate:", company, jobName, emailKey);

    const candidateCollection = collection(
      firestore,
      company,
      "Jobs",
      jobName,
      "Candidates",
      emailKey
    );

    const snapshot = await getDocs(candidateCollection);

    

    for (const document of snapshot.docs) {
      await deleteDoc(document.ref);
    }


    alert("Skill Graph removed successfully.");
    setInterviewError(null);
    setInterviewLoading(false);

const token = await user.getIdTokenResult();
const sanitizedEmail = token.claims.sanitizedEmail;

const sourcingDocRef = doc(
  firestore,
  "CandidateSourcing",
  sanitizedEmail
);

await updateDoc(sourcingDocRef, {
  isInterviewDone: false
});

    window.location.reload();

  } catch (error) {
    console.error(error);
    alert("Failed to delete application.");
  }
};

const handleDeletePsychometric = async () => {
  const confirmDelete = window.confirm(
    "Are you sure you want to remove your Psychometrics?"
  );
  if (!confirmDelete) return;

  try {
    const emailKey = user.email.replace(/\./g, "_");

    // Delete Pmetric candidate docs
    const candidateCollection = collection(
      firestore,
      "Pmetric",
      "Jobs",
      "Candidate Psychometric_ID=177215044969315pq0m6sh",
      "Candidates",
      emailKey
    );

    const snapshot = await getDocs(candidateCollection);
    for (const document of snapshot.docs) {
      await deleteDoc(document.ref);
    }

    // Reset isMetricDone in CandidateSourcing
    const sanitizedEmail = user.email
      .split('@')[0].replace(/\./g, '_') +
      user.email.split('@')[1].replace(/\./g, '_');

    const sourcingDocRef = doc(
      firestore,
      "CandidateSourcing",
      sanitizedEmail
    );
    await updateDoc(sourcingDocRef, {
      isMetricDone: false
    });

    alert("Psychometrics removed successfully.");
    window.location.reload();

  } catch (error) {
    console.error(error);
    alert("Failed to delete psychometrics.");
  }
};

  // Check if form is valid
  const isFormValid = cvUploaded &&
    formData.firstName && formData.lastName && formData.phone &&
    formData.linkedin && formData.location && 
    formData.desiredRoles && formData.salaryCurrency && formData.salaryAmount &&
    formData.salaryPeriod && formData.workLocationPreference && formData.joiningTimeline &&
    formData.termsAccepted;

  // Form context
  const getFormContext = () => {
    if (isEditMode) {
      return {
        welcomeTitle: 'Edit Your Internship Application',
        welcomeMessage: 'Update your internship application below.',
        submitButtonText: 'Save Changes',
        submitLoadingText: 'Saving Changes...'
      };
    } else if (isExistingCandidate) {
      return {
        welcomeTitle: 'Welcome Back!',
        welcomeMessage: welcomeMessage,
        submitButtonText: 'Update Application',
        submitLoadingText: 'Updating Application...'
      };
    } else {
      return {
        welcomeTitle: 'Welcome to NeoRecruit Talent Cloud',
        welcomeMessage: welcomeMessage,
        submitButtonText: 'Submit Application',
        submitLoadingText: 'Submitting Application...'
      };
    }
  };

  const formContext = getFormContext();

  return (
    <div className="candidate-form-container">
      {/* Header */}
      <div className="form-header">
        <div className="header-content">
          <div className="header-left">
            <div className="app-logo">
              <img 
                src="/Neodonya_Logo.png" 
                alt="Neodonya Recruit" 
                className="company-logo-header"
              />
            </div>
            <div className="header-subtitle">Talent Cloud</div>
          </div>
          <div className="user-info">
            <div className="user-avatar">
              <img src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email)}&background=6366f1&color=fff`} 
                   alt="User" />
            </div>
            <div className="user-details">
              <span className="user-name">{user.displayName || user.email}</span>
              <button onClick={handleLogout} className="logout-btn">
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="form-content">
        <div className="candidate-form">
          {/* Welcome Message */}
          {welcomeMessage && (
            <div className={`welcome-message ${isExistingCandidate && !isEditMode ? 'existing' : 'new'}`}>
              <h2>{formContext.welcomeTitle}</h2>
              <p>{formContext.welcomeMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            
            {/* Personal Information Section */}
            <div className="form-section">
              <div className="section-header">
                <span className="section-number">1</span>
                <h2>Personal Information</h2>
              </div>
              
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="firstName">
                    <span className="label-text">First Name</span>
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter your first name"
                    disabled={isReadOnly}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">
                    <span className="label-text">Last Name</span>
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Enter your last name"
                    disabled={isReadOnly}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">
                    <span className="label-text">Phone</span>
                    <span className="required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                    disabled={isReadOnly}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <span className="label-text">Email</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={user.email}
                    disabled
                    className="disabled-field"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="linkedin">
                    <span className="label-text">LinkedIn Profile</span>
                    <span className="required">*</span>
                  </label>
                  <input
                    type="url"
                    id="linkedin"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleInputChange}
                    placeholder="https://linkedin.com/in/yourprofile"
                    disabled={isReadOnly}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="location">
                    <span className="label-text">Where are you based? (City, Country)</span>
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="e.g., Chennai,Goa"
                    disabled={isReadOnly}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Education Section */}
            {/* <div className="form-section">
              <div className="section-header">
                <span className="section-number">2</span>
                <h2>Education Background</h2>
              </div>
              
              <div className="form-group">
                <label htmlFor="education">
                  <span className="label-text">Education Background (degree, current status, year of study/graduation)</span>
                  <span className="required">*</span>
                </label>
                <textarea
                  id="education"
                  name="education"
                  value={formData.education}
                  onChange={handleInputChange}
                  placeholder="e.g., Computer Science, Final year student, graduating 2025"
                  rows="3"
                  disabled={isReadOnly}
                  required
                />
              </div>
            </div> */}

            {/* Job Preferences Section */}
            <div className="form-section">
              <div className="section-header">
                <span className="section-number">2</span>
                <h2>Job Preferences</h2>
              </div>

              {/* Question 1: Desired Roles */}
              <div className="form-group">
                <label htmlFor="desiredRoles">
                  <span className="label-text">What roles are you looking out for and why?</span>
                  <span className="required">*</span>
                </label>
                <p className="field-description">Be as descriptive and specific as possible including seniority level, responsibilities, and focus areas (minimum 50 characters)</p>
                <textarea
                  id="desiredRoles"
                  name="desiredRoles"
                  value={formData.desiredRoles}
                  onChange={handleInputChange}
                  placeholder="e.g., Senior Full Stack Developer with 5+ years experience, focusing on React and Node.js. Looking for roles involving system architecture, mentoring, and building scalable B2B SaaS products..."
                  rows="6"
                  disabled={isReadOnly}
                  required
                />
                <div className="character-count">
                  {formData.desiredRoles.length} characters (minimum 50 required)
                </div>
              </div>

              {/* Question 2: Minimum Salary */}
              <div className="form-group">
                <label htmlFor="salaryCurrency">
                  <span className="label-text">Minimum Salary Expectation</span>
                  <span className="required">*</span>
                </label>
                <p className="field-description">Below which you will not consider a role</p>
                <div className="salary-group">
                  <div className="salary-field">
                    <label htmlFor="salaryCurrency" className="sub-label">Currency</label>
                    <select
                      id="salaryCurrency"
                      name="salaryCurrency"
                      value={formData.salaryCurrency}
                      onChange={handleInputChange}
                      disabled={isReadOnly}
                      required
                    >
                      <option value="">Select...</option>
                      {currencyOptions.map((currency, index) => (
                        <option key={index} value={currency}>{currency}</option>
                      ))}
                    </select>
                  </div>
                  <div className="salary-field">
                    <label htmlFor="salaryAmount" className="sub-label">Amount</label>
                    <input
                      type="number"
                      id="salaryAmount"
                      name="salaryAmount"
                      value={formData.salaryAmount}
                      onChange={handleInputChange}
                      placeholder="e.g., 1800000"
                      min="0"
                      step="1000"
                      disabled={isReadOnly}
                      required
                    />
                  </div>
                  <div className="salary-field">
                    <label htmlFor="salaryPeriod" className="sub-label">Period</label>
                    <select
                      id="salaryPeriod"
                      name="salaryPeriod"
                      value={formData.salaryPeriod}
                      onChange={handleInputChange}
                      disabled={isReadOnly}
                      required
                    >
                      <option value="">Select...</option>
                      {salaryPeriodOptions.map((period, index) => (
                        <option key={index} value={period}>{period}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Question 3: Remote vs Onsite */}
              <div className="form-group">
                <label htmlFor="workLocationPreference">
                  <span className="label-text">Remote vs Onsite Preference</span>
                  <span className="required">*</span>
                </label>
                <p className="field-description">Maximum 50 words (approximately 250 characters)</p>
                <textarea
                  id="workLocationPreference"
                  name="workLocationPreference"
                  value={formData.workLocationPreference}
                  onChange={handleInputChange}
                  placeholder="e.g., Prefer fully remote but open to hybrid with 2 days in office. Open to relocation for the right opportunity."
                  rows="3"
                  maxLength="250"
                  disabled={isReadOnly}
                  required
                />
                <div className={`character-count ${formData.workLocationPreference.length > 250 ? 'error' : formData.workLocationPreference.length > 200 ? 'warning' : ''}`}>
                  {formData.workLocationPreference.length}/250 characters
                </div>
              </div>

              {/* Question 4: Joining Timeline */}
              <div className="form-group">
                <label htmlFor="joiningTimeline">
                  <span className="label-text">If offered a role, how fast can you join from the date of offer?</span>
                  <span className="required">*</span>
                </label>
                <p className="field-description">Enter number of days (e.g., 30 for one month, 0 for immediate)</p>
                <div className="joining-timeline-input">
                  <input
                    type="number"
                    id="joiningTimeline"
                    name="joiningTimeline"
                    value={formData.joiningTimeline}
                    onChange={handleInputChange}
                    placeholder="30"
                    min="0"
                    max="365"
                    disabled={isReadOnly}
                    required
                  />
                  <span className="input-suffix">days</span>
                </div>
              </div>
            </div>

            {/* CV Upload Section */}
            <div className="form-section">
              <div className="section-header">
                <span className="section-number">3</span>
                <h2>Upload Resume</h2>
              </div>
              
              <div className={`cv-upload-area${isReadOnly ? ' cv-locked' : ''}`}>
                <input
                  type="file"
                  id="cv-file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  className="file-input"
                  disabled={isReadOnly}
                />
                <label htmlFor="cv-file" className={`file-drop-zone ${cvFile ? 'has-file' : ''}`}>
                  <div className="drop-zone-content">
                    {uploadLoading ? (
                      <div className="upload-progress">
                        <div className="spinner"></div>
                        <span>Uploading...</span>
                      </div>
                    ) : cvUploaded ? (
                      <div className="upload-success">
                        <div className="success-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        </div>
                        <div className="upload-text">
                          <span><strong>CV Uploaded Successfully</strong></span>
                          <small>{uploadedCvInfo?.fileName}</small>
                        </div>
                      </div>
                    ) : (
                      <div className="upload-prompt">
                        <div className="upload-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M21 15V19A2 2 0 0119 21H5A2 2 0 013 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M17 8L12 3L7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="upload-text">
                          <span>Drop your resume here or <strong>browse files</strong></span>
                          <small>PDF, DOC up to 5MB</small>
                        </div>
                      </div>
                    )}
                  </div>
                </label>

                {/* Extraction Progress Indicator */}
                {extractionProgress && (
                  <div className={`extraction-progress ${extractionProgress.stage}`}>
                    {extractionInProgress && (
                      <div className="extraction-spinner"></div>
                    )}
                    <div className="extraction-message">
                      {extractionProgress.stage === 'completed' && '✅ '}
                      {extractionProgress.stage === 'failed' && '⚠️ '}
                      {extractionProgress.stage === 'error' && '❌ '}
                      {extractionProgress.message}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Terms and Conditions Section */}
            <div className="form-section">
              <div className="section-header">
                <span className="section-number">4</span>
                <h2>Terms and Conditions</h2>
              </div>
              
              <div className="terms-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={handleInputChange}
                    disabled={isReadOnly}
                    required
                  />
                  <span className="checkmark"></span>
                  <span className="terms-text">
                    I agree to have my data reviewed and matched for current and future roles that align with my profile.
                    <span className="required">*</span>
                  </span>
                </label>
              </div>
            </div>

            {/* Submit Section */}
            <div className="form-actions">

              {submittedSuccess && isReadOnly ? (
                /* After submission */
                <div className="submission-success-section">
                  <div className="submission-success-message">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Application submitted successfully!
                  </div>

                  {/* Edit Application button always visible */}
                  <button
                    type="button"
                    onClick={() => setIsReadOnly(false)}
                    className="edit-application-btn"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Edit Application
                  </button>

                  {/* Interview section — conditional on isInterviewDone */}
                  {isInterviewDone ? (
                    /* Skills graph card */
                    <div className="skills-graph-card">
                      <div className="skills-graph-left">
                        <div className="skills-graph-avatar">
                          <img
                            src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email)}&background=4f46e5&color=fff`}
                            alt="User avatar"
                          />
                        </div>
                        <span className="skills-graph-name">
                          {user.displayName || existingData?.firstName || 'Candidate'} <span className="skills-graph-label">Skills Graph</span>
                        </span>
                      </div>
                      <div className="skills-graph-actions">
                        <button
                          type="button"
                          onClick={handleTakeInterview}
                          disabled={interviewLoading}
                          className="skills-update-btn"
                          title="Retake interview to update your skills graph"
                        >
                          {interviewLoading ? 'Loading...' : 'Update'}
                        </button>
                        <button
                          type="button"
                          className="skills-delete-btn"
                          title="Remove skills graph"
                          onClick={handleDeleteCandidate}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Interview CTA banner */
                    <div className="interview-cta-banner">
                      <p className="interview-cta-text">
                        {isExistingCandidate
                          ? 'Create your skills graph via our AI interview and improve your chances of getting selected.'
                          : 'Create your skills graph via our AI interview and improve your chances of getting selected.'}
                      </p>
                      <button
                        type="button"
                        onClick={handleTakeInterview}
                        disabled={interviewLoading}
                        className="take-interview-btn"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5 3l14 9-14 9V3z" fill="currentColor"/>
                        </svg>
                        {interviewLoading ? 'Loading...' : 'Take Interview'}
                      </button>
                    </div>
                  )}

                  {interviewError && (
                    <div className="interview-error">{interviewError}</div>
                  )}

                  {/* Psychometric section — always shows after submission */}
                  {/* {console.log('🧪 isMetricDone:', isMetricDone, '| isInterviewDone:', isInterviewDone)} */}
                   {isMetricDone ? (
  <div className="skills-graph-card">
    <div className="skills-graph-left">
      <div className="skills-graph-avatar">
        <img
          src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email)}&background=7c3aed&color=fff`}
          alt="User avatar"
        />
      </div>
      <span className="skills-graph-name">
        {user.displayName || existingData?.firstName || 'Candidate'} <span className="psychometric-label">Psychometrics</span>
      </span>
    </div>
    <div className="skills-graph-actions">
      <button
        type="button"
        onClick={handleTakePsychometric}
        disabled={psychometricLoading}
        className="skills-update-btn"
        title="Retake psychometric to update your profile"
      >
        {psychometricLoading ? 'Loading...' : 'Update'}
      </button>
      <button
        type="button"
        className="skills-delete-btn"
        title="Remove psychometrics"
        onClick={handleDeletePsychometric}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  </div>
                  ) : (
                    /* Psychometric CTA banner */
                    <div className="psychometric-cta-banner">
                      <p className="psychometric-cta-text">
                        Discover the role where your personality may be the best fit by taking our psychometric assessment
                      </p>
                      <button
                        type="button"
                        onClick={handleTakePsychometric}
                        disabled={psychometricLoading}
                        className="take-psychometric-btn"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5 3l14 9-14 9V3z" fill="currentColor"/>
                        </svg>
                        {psychometricLoading ? 'Loading...' : 'Take Psychometric'}
                      </button>
                    </div>
                  )}

                  {psychometricError && (
                    <div className="interview-error">{psychometricError}</div>
                  )}
                </div>
              ) : (
                /* Normal mode: show Submit / Update Application button */
                <>
                  <button
                    type="submit"
                    disabled={loading || !isFormValid}
                    className="submit-btn"
                  >
                    {loading ? (
                      <>
                        <div className="spinner"></div>
                        {formContext.submitLoadingText}
                      </>
                    ) : (
                      submittedSuccess ? 'Update Application' : formContext.submitButtonText
                    )}
                  </button>
                  {!isFormValid && (
                    <div className="validation-message">
                      Please complete all required fields, upload your resume, and accept the terms and conditions
                    </div>
                  )}
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CandidateForm;